/**
 * Which build this tab is running, and which one the server is handing out now.
 *
 * The CRA entry bundle is `main.<contenthash>.js`, so the filename *is* the build identity and
 * `index.html` is the only artefact that names it. See "Auto-reload on a new deploy" in
 * `apps/web/CLAUDE.md` — the check only works paired with the cache rules in `nginx/nginx.conf`.
 */

/** Loose about the hash on purpose: pinning CRA's eight hex chars would break on a webpack bump. */
const MAIN_BUNDLE_PATTERN = /\/static\/js\/(main\.[A-Za-z0-9_-]+\.js)/;

/** The bundle filename inside an `index.html` document or a `src` attribute, if there is one. */
export function parseBuildId(html: string): string | null {
  return MAIN_BUNDLE_PATTERN.exec(html)?.[1] ?? null;
}

/**
 * The build the current document loaded.
 *
 * `null` under `react-scripts start` and in jsdom, where the bundle is unhashed — and that is the
 * whole disable switch, so there is no `NODE_ENV` branch anywhere.
 */
export function readCurrentBuildId(): string | null {
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"));
  for (const script of scripts) {
    const buildId = parseBuildId(script.getAttribute("src") ?? "");
    if (buildId) return buildId;
  }
  return null;
}

/**
 * The build the server is serving, or `null` when the question cannot be answered right now.
 *
 * `null` rather than a throw on a non-200: the expected non-200 is nginx's 503 maintenance page
 * during a deploy, which is the one moment the answer would be least trustworthy anyway.
 */
export async function fetchDeployedBuildId(signal?: AbortSignal): Promise<string | null> {
  const base = process.env.PUBLIC_URL ?? "";
  // The query busts caches `cache: 'no-store'` does not reach — notably the company Apache proxy.
  const response = await fetch(`${base}/index.html?_=${Date.now()}`, {
    cache: "no-store",
    // Nothing here is per-user; omitting the cookie keeps the poll off the API's radar.
    credentials: "omit",
    signal,
  });
  if (!response.ok) return null;
  return parseBuildId(await response.text());
}
